<template>
  <v-dialog v-model="isOpen" max-width="800" persistent>
    <v-card class="agent-contact-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar">
              <i class="material-icons-round">support_agent</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Alex</h3>
              <p class="agent-title">AutoSyncX™ Agent Representative</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Mode Toggle -->
            <v-btn-toggle v-model="currentMode" mandatory class="mode-toggle">
              <v-btn value="chat" small dark>
                <i class="material-icons-round">chat</i>
                Chat
              </v-btn>
              <v-btn value="voice" small dark>
                <i class="material-icons-round">mic</i>
                Voice
              </v-btn>
            </v-btn-toggle>
            <!-- Close Button -->
            <v-btn
              icon
              @click="closeModal"
              class="close-btn"
            >
              <v-icon class="material-icons-round">close</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="modal-content">
        <!-- Chat Mode -->
        <div v-if="currentMode === 'chat'" class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div
              v-for="(message, index) in messages"
              :key="index"
              :class="['message', message.sender_type === 'user' ? 'user-message' : 'agent-message']"
            >
              <div class="message-content">
                <div class="message-text">{{ message.message }}</div>
                <div class="message-time">{{ formatTime(message.sent_at) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="typing-indicator">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="typing-text">Alex is typing...</span>
            </div>
          </div>
          
          <div class="chat-input">
            <!-- Photo Upload Section (shown when in photo_upload state) -->
            <div v-if="signupState === 'photo_upload' && !uploadedPhoto" class="photo-upload-section">
              <input
                type="file"
                ref="photoInput"
                accept="image/*"
                @change="handlePhotoSelect"
                style="display: none"
              />
              <v-btn
                color="blue"
                dark
                @click="$refs.photoInput.click()"
                class="upload-btn"
                block
              >
                <i class="material-icons-round mr-2">add_photo_alternate</i>
                Upload Profile Photo
              </v-btn>
              <p class="upload-hint">Click to browse or drag and drop an image</p>
            </div>

            <!-- Photo Preview (shown after photo selected) -->
            <div v-else-if="signupState === 'photo_upload' && uploadedPhoto" class="photo-preview-section">
              <div class="photo-preview">
                <img :src="photoPreview" alt="Profile Preview" />
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removePhoto"
                  class="remove-photo-btn"
                >
                  <i class="material-icons-round">close</i>
                </v-btn>
              </div>
              <v-btn
                color="green"
                @click="confirmPhotoUpload"
                :disabled="isTyping"
                block
                class="mt-3"
              >
                <i class="material-icons-round mr-2">check</i>
                Confirm Photo
              </v-btn>
            </div>

            <!-- Regular Text Input -->
            <v-text-field
              v-else
              v-model="currentMessage"
              placeholder="Type your message..."
              outlined
              dense
              hide-details
              @keyup.enter="sendMessage"
              :disabled="isTyping"
              class="message-input"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  @click="sendMessage"
                  :disabled="!currentMessage.trim() || isTyping"
                  class="send-btn"
                >
                  <i class="material-icons-round">send</i>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </div>

        <!-- Voice Mode -->
        <div v-else-if="currentMode === 'voice'" class="voice-container">
          <div class="voice-status">
            <div class="connection-status" :class="connectionStatus.type">
              <i class="material-icons-round">{{ connectionStatus.icon }}</i>
              <span>{{ connectionStatus.message }}</span>
            </div>
          </div>

          <!-- Voice Controls -->
          <div class="text-center mb-6">
            <div class="microphone-container">
              <v-btn
                :color="isVoiceCallActive ? 'success' : 'white'"
                icon
                size="x-large"
                :class="{ 'realtime-active': isVoiceCallActive }"
                @click="toggleVoiceConversation"
                :disabled="connectionStatus.type === 'error'"
                class="microphone-button"
              >
                <i class="material-icons-round" :style="{ color: isVoiceCallActive ? 'white' : '#333' }">{{ isVoiceCallActive ? 'mic' : 'mic_off' }}</i>
              </v-btn>

              <!-- Mute Button (only show when call is active) -->
              <v-btn
                v-if="isVoiceCallActive"
                :color="isMuted ? 'red' : 'grey'"
                icon
                dark
                size="large"
                @click="toggleMute"
                class="mute-button ml-4"
              >
                <i class="material-icons-round">{{ isMuted ? 'mic_off' : 'mic' }}</i>
              </v-btn>
            </div>

            <p class="text-h6 mt-4">
              {{ isVoiceCallActive ? 'Real-time conversation active' : 'Click to start voice conversation' }}
            </p>

            <p class="text-body-2 text-grey-darken-1">
              {{ connectionStatus.type === 'success' ? 'Connected to Alex from AutoSyncX™' : 'Connecting...' }}
            </p>
          </div>

          <!-- Voice Transcript -->
          <div v-if="voiceTranscript.length > 0" class="voice-transcript">
            <h4 class="transcript-title">Conversation</h4>
            <div class="transcript-messages">
              <div
                v-for="(message, index) in voiceTranscript"
                :key="index"
                :class="['transcript-message', message.role]"
              >
                <strong>{{ message.role === 'user' ? 'You' : 'Alex' }}:</strong>
                {{ message.content }}
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Conversation } from '@elevenlabs/client'

export default {
  name: 'AgentContactModal',
  props: {
    mode: {
      type: String,
      default: 'chat'
    },
    userId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isOpen: true,
      currentMode: this.mode,
      messages: [],
      currentMessage: '',
      isTyping: false,
      sessionId: null,
      conversationId: null,
      isVoiceCallActive: false,
      voiceTranscript: [],
      connectionStatus: {
        type: 'success',
        icon: 'check_circle',
        message: 'Connected'
      },
      // Voice agent data
      agentId: null,
      signedUrl: null,
      conversation: null,
      isSpeaking: false,
      isListening: false,
      isMuted: false,
      microphoneStream: null, // Store microphone stream for mute control
      // Agent chat signup data
      isInSignupFlow: false,
      signupSessionId: null,
      signupState: 'greeting',
      collectedData: {},
      registrationCompleted: false,
      uploadedPhoto: null,
      photoPreview: null,
      showPhotoUpload: false
    }
  },
  computed: {
    voiceIcon() {
      if (this.isSpeaking) return 'record_voice_over'
      if (this.isListening) return 'hearing'
      if (this.isVoiceCallActive) return 'phone_in_talk'
      return 'phone'
    },
    voiceStatusText() {
      if (this.isSpeaking) return 'Alex is speaking...'
      if (this.isListening) return 'Listening...'
      if (this.isVoiceCallActive) return 'Call Active'
      return 'Ready to Call'
    },
    voiceDescription() {
      if (this.isVoiceCallActive) return 'You are now connected with Alex. Speak naturally!'
      return 'Click to start a voice conversation with Alex, your AutoSyncX™ representative.'
    }
  },
  watch: {
    currentMode(newMode, oldMode) {
      console.log('🔄 Mode changed from', oldMode, 'to', newMode)

      // Stop voice conversation when switching away from voice mode
      if (oldMode === 'voice' && this.isVoiceCallActive) {
        console.log('🔇 Stopping voice conversation due to mode switch')
        this.stopConversation()
      }

      // Initialize the new mode
      if (newMode === 'chat') {
        this.initializeChat()
      } else if (newMode === 'voice') {
        this.initializeVoice()
      }
    }
  },
  mounted() {
    if (this.currentMode === 'chat') {
      this.initializeChat()
    } else if (this.currentMode === 'voice') {
      this.initializeVoice()
    }
  },

  beforeUnmount() {
    if (this.isVoiceCallActive) {
      this.stopConversation()
    }
  },
  methods: {
    closeModal() {
      this.isOpen = false
      this.$emit('close')
    },
    
    // Chat Methods
    async initializeChat() {
      try {
        console.log('🎤 Initializing chat with agent voice agent...')
        const response = await axios.post('/api/agent/voice-agent/create-conversation')

        if (response.data.flag === 1) {
          this.conversationId = response.data.data.conversation_id
          console.log('✅ Chat initialized:', this.conversationId)
        } else {
          // Fallback to session ID if API fails
          this.sessionId = this.generateSessionId()
        }

        // Add welcome message
        this.addMessage({
          sender_type: 'agent',
          message: '👋 Hi, I\'m Alex, your AutoSync Referring Agent Assistant.\n\nWe\'re currently in Beta and our first goal is to reach 1,000 signups. 🚀\n\nThe best part? 👉 You don\'t need to explain anything to anyone. Just share me (Alex), and I\'ll explain everything and enroll people for you.\n\nWould you like to get your digital referral card now?',
          sent_at: new Date().toISOString()
        })

      } catch (error) {
        console.error('❌ Error initializing chat:', error)
        // Fallback to session ID
        this.sessionId = this.generateSessionId()
        this.$toast.error('Failed to initialize chat. Please try again.')
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isTyping) return

      const userMessage = this.currentMessage.trim()
      this.currentMessage = ''

      // Add user message to chat
      this.addMessage({
        sender_type: 'user',
        message: userMessage,
        sent_at: new Date().toISOString()
      })

      // Simulate agent response
      this.simulateAgentResponse(userMessage)
    },

    addMessage(message) {
      this.messages.push(message)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    async simulateAgentResponse(userMessage) {
      this.isTyping = true

      try {
        // Check for signup intent
        const signupKeywords = ['yes', 'let\'s do it', 'get started', 'sign up', 'signup', 'join', 'register', 'digital card', 'referral card']
        const isSignupIntent = signupKeywords.some(keyword =>
          userMessage.toLowerCase().includes(keyword.toLowerCase())
        )

        if (this.isInSignupFlow || isSignupIntent) {
          // Handle agent chat signup with OpenAI
          await this.handleAgentChatSignup(userMessage)
          return
        }

        // Default greeting for non-signup messages
        setTimeout(() => {
          const response = '👋 Hi, I\'m Alex, your AutoSyncX™ Referring Agent Assistant.\n\nWe\'re currently in Beta and our first goal is to reach 1,000 signups. 🚀\n\nThe best part? 👉 You don\'t need to explain anything to anyone. Just share me (Alex), and I\'ll explain everything and enroll people for you.\n\nWould you like to get your digital referral card now?'

          this.addMessage({
            sender_type: 'agent',
            message: response,
            sent_at: new Date().toISOString()
          })

          this.isTyping = false
        }, 1000)
      } catch (error) {
        console.error('❌ Error in agent response:', error)
        this.isTyping = false
      }
    },

    async handleAgentChatSignup(userMessage) {
      try {
        if (!this.isInSignupFlow) {
          // Initialize agent chat signup
          await this.initializeAgentChatSignup()
        }

        // Process the message through OpenAI
        await this.processAgentChatMessage(userMessage)

      } catch (error) {
        console.error('❌ Error in agent chat signup:', error)
        this.addMessage({
          sender_type: 'agent',
          message: 'Sorry, there was an error processing your request. Please try again.',
          sent_at: new Date().toISOString()
        })
      } finally {
        this.isTyping = false
      }
    },

    async initializeAgentChatSignup() {
      try {
        const response = await axios.post('/api/agent-chat-signup/init')

        if (response.data.flag === 1) {
          this.signupSessionId = response.data.session_id
          this.isInSignupFlow = true
          console.log('✅ Agent chat signup initialized:', this.signupSessionId)
        } else {
          throw new Error('Failed to initialize agent chat signup')
        }
      } catch (error) {
        console.error('❌ Error initializing agent chat signup:', error)
        throw error
      }
    },

    async processAgentChatMessage(userMessage) {
      try {
        console.log('📤 Sending message to API:', userMessage)

        const response = await axios.post('/api/agent-chat-signup/message', {
          session_id: this.signupSessionId,
          message: userMessage
        })

        console.log('📥 API Response:', response.data)

        if (response.data.flag === 1) {
          this.signupState = response.data.conversation_state
          this.collectedData = response.data.collected_data

          console.log('📊 Updated state:', this.signupState)
          console.log('📊 Collected data:', this.collectedData)

          // Add AI response
          this.addMessage({
            sender_type: 'agent',
            message: response.data.message,
            sent_at: new Date().toISOString()
          })

          // Check if registration is complete (and not already completed)
          if (response.data.is_complete && !this.registrationCompleted) {
            console.log('✅ Registration complete, calling completeAgentChatSignup...')
            await this.completeAgentChatSignup()
          } else if (response.data.is_complete && this.registrationCompleted) {
            console.log('ℹ️ Registration already completed, skipping duplicate registration')
          }
        } else {
          throw new Error(response.data.message || 'Failed to process message')
        }
      } catch (error) {
        console.error('❌ Error processing agent chat message:', error)
        console.error('❌ Error details:', error.response?.data || error.message)
        throw error
      }
    },

    async completeAgentChatSignup() {
      try {
        console.log('🎯 Starting registration completion...')
        console.log('📸 Uploaded photo:', this.uploadedPhoto ? this.uploadedPhoto.name : 'No photo')
        console.log('👤 Referring user ID (ref_id):', this.userId)

        // Prepare form data with photo
        const formData = new FormData()
        formData.append('session_id', this.signupSessionId)

        // Add ref_id (referring agent's user_id) if provided
        if (this.userId) {
          formData.append('ref_id', this.userId)
          console.log('✅ Added ref_id to form data:', this.userId)
        }

        if (this.uploadedPhoto) {
          formData.append('file_photo', this.uploadedPhoto)
          console.log('✅ Photo added to form data')
        } else {
          console.warn('⚠️ No photo uploaded!')
        }

        console.log('📤 Sending registration request...')
        const response = await axios.post('/api/agent-chat-signup/complete', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        console.log('📥 Registration response:', response)
        console.log('📥 Registration response data:', response.data)
        console.log('📥 Response data type:', typeof response.data)

        // Handle response data
        let responseData = response.data

        // If response.data is a string, try to parse it
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
            console.log('✅ Parsed string response to object:', responseData)
          } catch (e) {
            console.error('❌ Failed to parse response string:', e)
            throw new Error('Invalid response format from server')
          }
        }

        console.log('📥 Final response data:', responseData)
        console.log('📥 Response flag:', responseData.flag)

        if (responseData.flag === 1) {
          this.registrationCompleted = true
          console.log('✅ Agent registration completed:', responseData)

          // Format referral link as subdomain
          const domain = responseData.domain
          const referralLink = `https://${domain}.autosyncx.com`

          this.addMessage({
            sender_type: 'agent',
            message: `All set! 🎯 I've created your digital card.\n\nHere's your referral link:\n👉 ${referralLink}\n\nNow, just share me (Alex) with others. I'll explain everything and sign them up under you. The bigger your team, the more you'll earn.`,
            sent_at: new Date().toISOString()
          })

          this.$toast.success('Welcome to AutoSyncX™! Your referral card has been created.')

          // Emit signup complete event with user data
          this.$emit('signup-complete', {
            user: responseData.data,
            referral_link: referralLink,
            domain: domain
          })
        } else if (responseData.flag === 3) {
          // Email already exists
          this.addMessage({
            sender_type: 'agent',
            message: 'It looks like you already have an account with that email. Would you like to try a different email address?',
            sent_at: new Date().toISOString()
          })
        } else if (responseData.flag === 0) {
          // Validation or other error
          const errorMessage = responseData.message || 'Registration failed'
          console.error('❌ Registration error:', errorMessage, responseData.errors)
          this.addMessage({
            sender_type: 'agent',
            message: `Sorry, there was an issue: ${errorMessage}. Please try again.`,
            sent_at: new Date().toISOString()
          })
        } else {
          throw new Error(responseData.message || 'Failed to complete registration')
        }
      } catch (error) {
        console.error('❌ Error completing agent chat signup:', error)
        console.error('❌ Error response:', error.response)

        let errorMessage = 'Sorry, there was an error completing your registration. Please try again or contact support.'

        // Try to extract error message from response
        if (error.response && error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }

        this.addMessage({
          sender_type: 'agent',
          message: errorMessage,
          sent_at: new Date().toISOString()
        })
      }
    },

    // Photo Upload Methods
    handlePhotoSelect(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          this.$toast.error('Please select a valid image file')
          return
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$toast.error('Image size must be less than 5MB')
          return
        }

        this.uploadedPhoto = file

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          this.photoPreview = e.target.result
        }
        reader.readAsDataURL(file)

        console.log('✅ Photo selected:', file.name)
      }
    },

    removePhoto() {
      this.uploadedPhoto = null
      this.photoPreview = null
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = ''
      }
    },

    async confirmPhotoUpload() {
      if (!this.uploadedPhoto) {
        this.$toast.error('Please select a photo first')
        return
      }

      try {
        this.isTyping = true

        // Add user confirmation message
        this.addMessage({
          sender_type: 'user',
          message: 'Photo uploaded ✓',
          sent_at: new Date().toISOString()
        })

        // Process through OpenAI to move to complete state
        await this.processAgentChatMessage('Photo uploaded and confirmed')
      } catch (error) {
        console.error('❌ Error confirming photo upload:', error)
        this.$toast.error('Failed to process photo upload')
      } finally {
        this.isTyping = false
      }
    },

    // Voice Methods
    async initializeVoice() {
      this.connectionStatus = {
        type: 'info',
        message: 'Connecting to voice agent...'
      }

      try {
        const response = await axios.post('/api/agent/voice-agent/create-conversation')

        if (response.data.flag === 1 && response.data.data) {
          this.agentId = response.data.data.agent_id
          this.signedUrl = response.data.data.signed_url

          console.log('📋 Agent ID:', this.agentId)
          console.log('📋 Signed URL:', this.signedUrl)

          this.connectionStatus = {
            type: 'success',
            message: 'Ready to start voice conversation!'
          }

          // Don't initialize microphone upfront - do it when user clicks button
          console.log('✅ Voice agent ready - microphone will be initialized when user clicks button')

        } else {
          throw new Error('Voice agent not available')
        }
      } catch (error) {
        console.error('❌ Error initializing voice:', error)
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to connect to voice agent'
        }
        this.$toast.error('Failed to initialize voice. Please try chat mode.')
      }
    },

    toggleVoiceConversation() {
      if (this.isVoiceCallActive) {
        this.stopConversation()
      } else {
        this.startConversation()
      }
    },

    async startConversation() {
      if (!this.signedUrl) {
        this.$toast.error('Voice agent not ready. Please try again.')
        return
      }

      try {
        console.log('🎤 Starting ElevenLabs conversation...')

        // Get microphone access first
        try {
          this.microphoneStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          })
          console.log('✅ Microphone access granted')
          console.log('📋 Microphone stream tracks:', this.microphoneStream.getAudioTracks())
        } catch (micError) {
          console.error('❌ Microphone access denied:', micError)
          this.$toast.error('Microphone access is required for voice conversation')
          return
        }

        // Start ElevenLabs conversation using the official SDK
        this.conversation = await Conversation.startSession({
          signedUrl: this.signedUrl,
          clientTools: {
            // Pass custom audio stream if supported
            audioStream: this.microphoneStream
          },
          onConnect: () => {
            console.log('✅ ElevenLabs conversation connected')
            this.isVoiceCallActive = true
            this.connectionStatus = {
              type: 'success',
              message: 'Real-time conversation active - speak naturally!'
            }
            this.$toast.success('Voice conversation started! You can now talk with Alex.')
          },
          onDisconnect: () => {
            console.log('🔌 ElevenLabs conversation disconnected')
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'info',
              message: 'Conversation ended'
            }

            // Stop microphone stream
            if (this.microphoneStream) {
              this.microphoneStream.getTracks().forEach(track => track.stop())
              this.microphoneStream = null
            }
          },
          onError: (error) => {
            console.error('❌ ElevenLabs conversation error:', error)
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'error',
              message: 'Connection error'
            }
            this.$toast.error('Voice conversation error. Please try again.')

            // Stop microphone stream
            if (this.microphoneStream) {
              this.microphoneStream.getTracks().forEach(track => track.stop())
              this.microphoneStream = null
            }
          },
          onModeChange: (mode) => {
            console.log('🔄 ElevenLabs mode change:', mode)
            this.isSpeaking = mode.mode === 'speaking'
            this.isListening = mode.mode === 'listening'
          }
        })

      } catch (error) {
        console.error('❌ Error starting conversation:', error)
        this.$toast.error('Failed to start voice conversation. Please try again.')
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to start conversation'
        }
      }
    },

    async stopConversation() {
      try {
        if (this.conversation) {
          await this.conversation.endSession()
          this.conversation = null
        }

        // Stop microphone stream
        if (this.microphoneStream) {
          this.microphoneStream.getTracks().forEach(track => track.stop())
          this.microphoneStream = null
          console.log('🎤 Microphone stream stopped')
        }

        this.isVoiceCallActive = false
        this.isSpeaking = false
        this.isListening = false
        this.isMuted = false
        this.connectionStatus = {
          type: 'info',
          message: 'Conversation ended'
        }
        console.log('🔌 Voice conversation stopped')
      } catch (error) {
        console.error('❌ Error stopping conversation:', error)
        this.isMuted = false

        // Ensure microphone is stopped even on error
        if (this.microphoneStream) {
          this.microphoneStream.getTracks().forEach(track => track.stop())
          this.microphoneStream = null
        }
      }
    },

    async toggleMute() {
      if (!this.conversation || !this.isVoiceCallActive) {
        console.warn('⚠️ No active conversation to mute')
        return
      }

      try {
        console.log('🔄 Toggle mute called. Current state:', this.isMuted)

        if (this.isMuted) {
          // Unmute - use ElevenLabs SDK method
          console.log('🎤 Unmuting microphone using setMicMuted(false)...')
          this.conversation.setMicMuted(false)
          this.isMuted = false
          this.$toast.info('Microphone unmuted')
          console.log('✅ Microphone unmuted')
        } else {
          // Mute - use ElevenLabs SDK method
          console.log('🔇 Muting microphone using setMicMuted(true)...')
          this.conversation.setMicMuted(true)
          this.isMuted = true
          this.$toast.info('Microphone muted')
          console.log('✅ Microphone muted')
        }
      } catch (error) {
        console.error('❌ Error toggling mute:', error)
        this.$toast.error('Failed to toggle microphone')
      }
    },

    // Utility Methods
    generateSessionId() {
      return 'agent_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.agent-contact-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 20px 24px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.agent-details h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.agent-details p {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mode-toggle {
  background: blue;
  border-radius: 8px;
}

.mode-toggle .v-btn {
  color: white !important;
  font-size: 0.8rem !important;
  padding: 8px 12px !important;
}

.mode-toggle .v-btn--active {
  background: orange !important;
}

.close-btn {
  color: white !important;
}

.modal-content {
  padding: 0;
  height: 500px;
}

/* Chat Styles */
.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.user-message {
  justify-content: flex-end;
}

.agent-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 16px;
  position: relative;
}

.user-message .message-content {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.agent-message .message-content {
  background: white;
  color: #333;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 4px;
}

.message-text {
  font-size: 0.95rem;
  line-height: 1.4;
  margin-bottom: 4px;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  max-width: 120px;
  border: 1px solid #e9ecef;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background: #007bff;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

.typing-text {
  font-size: 0.8rem;
  color: #6c757d;
}

.chat-input {
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #e9ecef;
}

.message-input {
  border-radius: 24px;
}

.send-btn {
  color: #007bff !important;
}

/* Photo Upload Styles */
.photo-upload-section {
  padding: 20px;
  text-align: center;
}

.upload-btn {
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 8px 0 0 0;
}

.photo-preview-section {
  padding: 20px;
  text-align: center;
}

.photo-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
}

.photo-preview img {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

.remove-photo-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Voice Styles */
.voice-container {
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.voice-status {
  margin-bottom: 30px;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.connection-status.success {
  background: #d4edda;
  color: #155724;
}

.connection-status.error {
  background: #f8d7da;
  color: #721c24;
}

.microphone-container {
  margin-bottom: 20px;
}

.microphone-button {
  width: 80px !important;
  height: 80px !important;
  border: 4px solid #e9ecef !important;
  transition: all 0.3s ease !important;
}

.microphone-button.realtime-active {
  border-color: #28a745 !important;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(40, 167, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
}

.voice-transcript {
  width: 100%;
  max-width: 600px;
  margin-top: 30px;
}

.transcript-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1a202c;
}

.transcript-messages {
  background: white;
  border-radius: 12px;
  padding: 16px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
}

.transcript-message {
  margin-bottom: 12px;
  font-size: 0.9rem;
  line-height: 1.4;
}

.transcript-message:last-child {
  margin-bottom: 0;
}

.transcript-message.user {
  color: #007bff;
}

.transcript-message.assistant {
  color: #495057;
}

/* Responsive Design */
@media (max-width: 600px) {
  .modal-content {
    height: 400px;
  }

  .header-actions {
    flex-direction: column;
    gap: 8px;
  }

  .mode-toggle {
    order: 2;
  }

  .close-btn {
    order: 1;
    align-self: flex-end;
  }

  .message-content {
    max-width: 85%;
  }

  .microphone-button {
    width: 60px !important;
    height: 60px !important;
  }
}
</style>
